/**
 * staffActivityLog.js
 *
 * Per-staff behavioural log for the CRM (/crm/*).
 * Tracks internal staff logins, account views, and is called by the server-side
 * trigger for write operations.
 *
 * Append-only: one doc per occurrence.
 *
 *   staff_activity/{autoId}
 *   { actorUid, actorEmail, actorName, actionType, accountId?, targetCollection?,
 *     targetDocId?, summary?, route?, timestamp }
 *
 * Writes are best-effort: tracking must never break the page the staff is on.
 */

import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export const STAFF_ACTIVITY_COLLECTION = 'staff_activity';

export const ACTION_TYPES = {
  LOGIN: 'login',
  ACCOUNT_VIEW: 'account_view',
  NOTE_ADDED: 'note_added',
  NOTE_EDITED: 'note_edited',
  STAGE_CHANGED: 'stage_changed',
  STATUS_CHANGED: 'status_changed',
  HOURS_LOGGED: 'hours_logged',
  DOC_UPLOADED: 'doc_uploaded',
  ACCOUNT_UPDATED: 'account_updated',
};

const DUPLICATE_WINDOW_MS = 2000;
const lastWriteByKey = new Map();

function isDuplicate(uid, key) {
  const cacheKey = `${uid}:${key}`;
  const now = Date.now();
  const previous = lastWriteByKey.get(cacheKey);
  if (previous && now - previous < DUPLICATE_WINDOW_MS) return true;
  lastWriteByKey.set(cacheKey, now);
  return false;
}

/**
 * Record a staff action. Each call creates its own row.
 *
 * @param {object} actor - { uid, email, name } of the internal user
 * @param {object} options
 * @param {string} options.actionType - One of ACTION_TYPES
 * @param {string} [options.accountId] - The account this action relates to
 * @param {string} [options.targetCollection] - Collection being written to
 * @param {string} [options.targetDocId] - Document being written to
 * @param {string} [options.summary] - Short description of what changed
 * @param {string} [options.route] - The /crm route this happened on
 */
export async function logStaffActivity(actor, {
  actionType,
  accountId = null,
  targetCollection = null,
  targetDocId = null,
  summary = null,
  route = null,
} = {}) {
  const uid = actor?.uid;
  if (!uid || !actionType) return;

  const key = `${actionType}_${accountId || ''}_${targetDocId || ''}_${route || ''}`;
  if (isDuplicate(uid, key)) return;

  try {
    const entry = {
      actorUid: uid,
      actorEmail: actor.email || null,
      actorName: actor.name || null,
      actionType,
      timestamp: serverTimestamp(),
    };

    if (accountId) entry.accountId = accountId;
    if (targetCollection) entry.targetCollection = targetCollection;
    if (targetDocId) entry.targetDocId = targetDocId;
    if (summary) entry.summary = String(summary).slice(0, 500);
    if (route) entry.route = route;

    await addDoc(collection(db, STAFF_ACTIVITY_COLLECTION), entry);
  } catch (error) {
    console.warn('[staffActivity] failed to log', actionType, error);
  }
}

/**
 * Log a CRM login event.
 * @param {object} actor - { uid, email, name }
 */
export function logStaffLogin(actor) {
  void logStaffActivity(actor, {
    actionType: ACTION_TYPES.LOGIN,
    summary: 'CRM sign-in',
    route: '/crm',
  });
}

/**
 * Log viewing an account (lead or client).
 * @param {object} actor - { uid, email, name }
 * @param {string} accountId
 * @param {string} accountType - 'lead' or 'client'
 * @param {string} companyName
 */
export function logAccountView(actor, accountId, accountType, companyName) {
  const type = accountType === 'lead' || accountType === 'Leads' ? 'lead' : 'client';
  void logStaffActivity(actor, {
    actionType: ACTION_TYPES.ACCOUNT_VIEW,
    accountId,
    summary: `Opened ${type}: ${companyName || accountId}`,
    route: `/crm/${type}/${accountId}`,
  });
}
